import React, { useCallback, useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import MonthPicker from '../components/MonthPicker';
import DownloadMenu from '../components/DownloadMenu';
import DetailsToggle from '../components/DetailsToggle';
import FlowTabs from '../components/flow/FlowTabs';
import ReportBody from '../components/report/ReportBody';
import FreshnessNote from '../components/FreshnessNote';
import KeptCopies from '../components/report/KeptCopies';
import { HALVES, HALF_LABEL, Half, TechReport } from '../components/report/types';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDetails } from '../hooks/useDetails';
import { usePeriodParams } from '../hooks/usePeriodParams';
import { useTabParam } from '../hooks/useTabParam';
import { navLabel } from '../utils/navigation';
import { saveBlob } from '../utils/download';
import * as fmt from '../lib/format';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const FILE: Record<'docx' | 'md', string> = { docx: 'docx', md: 'md' };

// The Tech report: the mid-month and monthly report, made from Jira, for the leader to send. The page is live; a copy that
// is sent is kept exactly as it went. Leaders and admins only.
export default function ReportPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { period, current, isCurrent, setPeriod } = usePeriodParams();
  // Mid-month until the 16th of the open month, the whole month after that and for any month that is over.
  const fallback: Half = isCurrent && new Date().getDate() <= 15 ? '1' : 'full';
  const [half, setHalf] = useTabParam<Half>(HALVES, fallback, 'half');

  const [report, setReport] = useState<TechReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [roadblocks, setRoadblocks] = useState('');
  const [busy, setBusy] = useState(false);
  const [keptVersion, setKeptVersion] = useState(0);
  const [showHidden, setShowHidden] = useDetails('hidden');

  const load = useCallback(async () => {
    try {
      const r = await apiService.getTechReport(period, half);
      if (!r.success) throw new Error(r.error);
      setReport(r.data);
      setRoadblocks(r.data.roadblocks);
      setError(null);
    } catch (e: any) {
      setReport(null);
      setError(e?.response?.data?.error || e?.message || 'Could not read the report');
    } finally {
      setLoading(false);
    }
  }, [period, half]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const saveRoadblocks = async () => {
    if (!report || roadblocks === report.roadblocks) return;
    try {
      const r = await apiService.saveTechReportRoadblocks(period, half, roadblocks);
      if (!r.success) throw new Error(r.error);
      setReport({ ...report, roadblocks: r.data.roadblocks });
      showToast('success', 'Roadblocks saved');
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not save the roadblocks');
    }
  };

  const download = async (format: 'docx' | 'md') => {
    setBusy(true);
    try {
      saveBlob(await apiService.downloadTechReport(period, half, format), `tech-report-${period}-${half === '1' ? 'mid-month' : half === '2' ? 'second-half' : 'month'}.${FILE[format]}`);
    } catch (e: any) {
      showToast('error', e?.message || 'Could not make the file');
    } finally {
      setBusy(false);
    }
  };

  const keep = async (format: 'docx' | 'md') => {
    setBusy(true);
    try {
      const r = await apiService.keepTechReport(period, half, format);
      if (!r.success) throw new Error(r.error);
      showToast('success', `Kept ${r.data.filename}`);
      setKeptVersion((v) => v + 1);
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not keep the copy');
    } finally {
      setBusy(false);
    }
  };

  const putBack = async (key: string) => {
    try {
      const r = await apiService.updatePlanNote(key, { hide_in_report: false });
      if (!r.success) throw new Error(r.error);
      void load();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not put it back');
    }
  };

  const items = (act: (f: 'docx' | 'md') => void) => [
    { key: 'docx', title: 'Word (.docx)', hint: 'To send: the report with the Jira keys as links', onSelect: () => act('docx') },
    { key: 'md', title: 'Markdown (.md)', hint: 'The same report as plain text', onSelect: () => act('md') },
  ];

  const tabs = HALVES.map((h) => ({ id: h, label: HALF_LABEL[h] }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60 print:bg-white">
      <div className="print:hidden">
        <PageHeader title={navLabel('/report')} subtitle="The mid-month and monthly report, from Jira" icon={FileText} />
      </div>
      <div className="p-4 sm:p-6">
        <div className="mx-auto max-w-6xl">
          <div className="mb-4 flex flex-wrap items-center gap-3 print:hidden">
            <MonthPicker value={period} max={current} onChange={setPeriod} className="" />
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <DownloadMenu busy={busy} disabled={!report} items={items(download)} />
              <DownloadMenu busy={busy} disabled={!report} label="Keep a copy" items={items(keep)} />
            </div>
          </div>
          <FreshnessNote className="mb-3" />
          <div className="print:hidden">
            <FlowTabs tabs={tabs} active={half} onChange={(id) => setHalf(id as Half)} label="Part of the month" idPrefix="report" />
          </div>

          {error && (
            <div role="alert" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-rose-50 p-4 text-sm text-rose-800 ring-1 ring-rose-200">
              <span>{error}</span>
              <button type="button" onClick={() => void load()} className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 font-medium text-rose-800 hover:bg-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500">Try again</button>
            </div>
          )}
          {loading && !report && !error && <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>Reading the report…</div>}

          {report && (
            <div role="tabpanel" id={`report-panel-${half}`} aria-labelledby={`report-tab-${half}`}>
              <h2 className="mb-1 text-xl font-semibold text-gray-900">{`Tech Department - ${report.period.title}`}</h2>
              <p className="mb-5 text-sm text-gray-600">
                {fmt.counted(report.counts.completed, 'task')} completed, {fmt.count(report.counts.ongoing)} ongoing, {fmt.count(report.counts.planned)} planned, read from Jira as at {report.period.as_at_label}. Only the remarks and the roadblocks are written by hand.
              </p>
              <ReportBody report={report} onChanged={() => void load()} roadblocks={roadblocks} onRoadblocks={setRoadblocks} onSaveRoadblocks={saveRoadblocks} />

              {report.hidden.length > 0 && (
                <section className={`${CARD} mb-5 p-5 print:hidden`} aria-label="Lines left out">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-base font-semibold text-gray-900">Left out of the report ({fmt.count(report.hidden.length)})</h2>
                    <DetailsToggle open={showHidden} onChange={setShowHidden} showLabel="Show them" hideLabel="Hide them" />
                  </div>
                  {showHidden && (
                    <ul className="mt-2 divide-y divide-gray-100 text-sm">
                      {report.hidden.map((h) => (
                        <li key={h.key} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2">
                          <span className="min-w-0 break-words text-gray-900">{h.project_name} | {h.feature}</span>
                          <button type="button" onClick={() => putBack(h.key)} className="rounded px-1.5 py-1 text-xs font-medium text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label={`Put back ${h.feature}`}>Put back</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}
            </div>
          )}

          <KeptCopies month={period} isAdmin={user?.role === 'admin'} reloadKey={keptVersion} />
        </div>
      </div>
    </div>
  );
}
